import { forwardRef } from 'react'
import { cn } from '@/lib/utils/cn'

export type ChipVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'accent'
export type ChipSize = 'md' | 'xs'

export interface ChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: ChipVariant
  size?: ChipSize
  /** Show a leading status dot. */
  dot?: boolean
}

const variantBase: Record<ChipVariant, string> = {
  success: 'bg-success-bg text-success-text border-success-border',
  warning: 'bg-warning-bg text-warning-text border-warning-border',
  danger: 'bg-danger-bg text-danger-text border-danger-border',
  info: 'bg-info-bg text-info-text border-info-border',
  neutral: 'bg-neutral-bg text-neutral-text border-neutral-border',
  accent: 'bg-accent-subtle text-accent-text border-accent-muted',
}

const variantDot: Record<ChipVariant, string> = {
  success: 'bg-success-dot',
  warning: 'bg-warning-dot',
  danger: 'bg-danger-dot',
  info: 'bg-info-dot',
  neutral: 'bg-neutral-dot',
  accent: 'bg-accent',
}

export const Chip = forwardRef<HTMLSpanElement, ChipProps>(function Chip(
  { variant = 'neutral', size = 'md', dot = false, className, children, ...rest },
  ref,
) {
  const isXs = size === 'xs'
  return (
    <span
      ref={ref}
      data-variant={variant}
      data-size={size}
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-xs border font-medium',
        isXs
          ? 'h-[var(--chip-height-xs)] gap-1 px-1.5 text-2xs'
          : 'h-[var(--chip-height)] gap-1.5 px-2.5 text-xs',
        variantBase[variant],
        className,
      )}
      {...rest}
    >
      {dot ? (
        <span
          aria-hidden="true"
          className={cn(
            'inline-block flex-shrink-0 rounded-full',
            isXs ? 'h-1 w-1' : 'h-1.5 w-1.5',
            variantDot[variant],
          )}
        />
      ) : null}
      {children}
    </span>
  )
})

/**
 * Vertical stack of chips (schedule + coverage, etc.) used in DataTable state
 * cells. See Principle 8 and 01-layout-system.md DataView spec.
 */
export function StateStack({ children, className }: { children: React.ReactNode; className?: string }) {
  return <span className={cn('inline-flex flex-col items-start gap-0.5', className)}>{children}</span>
}
